// Loads the player/ghost car model (public/models/car.glb, made by scripts/prepare-car.mjs).
//
// The GLB is authored in glTF space with the car's origin at the CG (rear axle at
// z = -L*frontWeight, front axle at z = +L*(1-frontWeight)), +z forward, y up, tyres
// on y = 0. Nodes: body, wheel_FL, wheel_FR, wheel_RL, wheel_RR (wheel pivots at the
// wheel centres). See docs/research/car-model-prep.md.
//
// Babylon's glTF loader adds a mirroring "__root__" node (right- to left-handed), which
// would invert steering angles if the game rotated the wheel nodes directly. So each
// wheel is re-hung under two plain TransformNodes in the car's own frame:
//   root -> wheel_XX (position = wheel centre, rotation.y = steer)
//        -> wheel_XX-spin (rotation.x = roll angle) -> wheel mesh.
// Car-frame conventions are then the same as the rest of the game: +x right, +y up,
// +z forward; positive rotation.y steers right, positive rotation.x rolls forward.

import type { Scene } from '@babylonjs/core/scene';
import type { AssetContainer } from '@babylonjs/core/assetContainer';
import { Material } from '@babylonjs/core/Materials/material';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { BaseTexture } from '@babylonjs/core/Materials/Textures/baseTexture';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { LoadAssetContainerAsync } from '@babylonjs/core/Loading/sceneLoader';
// Side effects: registers the glTF 2.0 loader (and the .glb SceneLoader plugin) only.
import '@babylonjs/loaders/glTF/2.0/glTFLoader';
// Glb uses quantized attributes; extensions aren't registered unless imported.
import { RegisterKHR_mesh_quantization } from '@babylonjs/loaders/glTF/2.0/Extensions/KHR_mesh_quantization.pure';

RegisterKHR_mesh_quantization();

export const CAR_MODEL_URL = `${import.meta.env.BASE_URL}models/car.glb`;

/** Wheel radii baked into the model (m): Pirelli 2026 sizes, 705 mm front / 710 mm rear. */
export const WHEEL_RADIUS = { front: 0.3525, rear: 0.355 } as const;

export interface CarVisual {
  /** Position/rotate this node; the car's origin is the CG on the ground plane. */
  root: TransformNode;
  /** Steering pivots (FL, FR): set `rotation.y` to the road-wheel angle. */
  frontWheels: TransformNode[];
  /** Spin nodes (FL, FR, RL, RR): `rotation.x` is the rolling angle. Use {@link spinWheels}. */
  wheels: TransformNode[];
  /** Every material of this car (each car gets its own copies), e.g. to fade the ghost. */
  materials: Material[];
  /** Rolling radius per entry of `wheels`. */
  wheelRadii: number[];
}

export interface CarModelOptions {
  /** < 1 makes a translucent, pale "ghost" car (materials alpha-blended). */
  alpha?: number;
  /** Ghost tint (multiplies the texture); default pale blue. */
  tint?: Color3;
  /** Override the model: a URL, or the GLB bytes (tests/tools). */
  source?: string | ArrayBufferView;
}

const WHEEL_NAMES = ['wheel_FL', 'wheel_FR', 'wheel_RL', 'wheel_RR'] as const;

// One parsed GLB per scene and source; each car instantiates clones (shared geometry/textures).
const containers = new WeakMap<Scene, Map<string | ArrayBufferView, Promise<AssetContainer>>>();

function loadContainer(scene: Scene, source: string | ArrayBufferView): Promise<AssetContainer> {
  let bySource = containers.get(scene);
  if (!bySource) {
    bySource = new Map();
    containers.set(scene, bySource);
  }
  let p = bySource.get(source);
  if (!p) {
    p = LoadAssetContainerAsync(source, scene, { pluginExtension: '.glb' }).then((c) => {
      toStandardMaterials(c, scene);
      return c;
    });
    bySource.set(source, p);
  }
  return p;
}

/**
 * Swap the loader's PBR materials for StandardMaterials: cheaper on phones and lit the
 * same way as the rest of the (StandardMaterial) scene. Shininess comes from the glTF roughness.
 */
function toStandardMaterials(c: AssetContainer, scene: Scene): void {
  const swapped = new Map<Material, StandardMaterial>();
  for (const mesh of c.meshes) {
    const src = mesh.material;
    if (!src) continue;
    let std = swapped.get(src);
    if (!std) {
      // Duck-typed so the PBR class isn't imported here.
      const pbr = src as unknown as { albedoTexture?: BaseTexture | null; albedoColor?: Color3; roughness?: number | null };
      std = new StandardMaterial(src.name, scene);
      scene.removeMaterial(std);
      c.materials.push(std);
      if (pbr.albedoTexture) std.diffuseTexture = pbr.albedoTexture;
      if (pbr.albedoColor) std.diffuseColor = pbr.albedoColor.clone();
      const rough = pbr.roughness ?? 0.5;
      const gloss = 1 - rough;
      std.specularColor = new Color3(0.5, 0.5, 0.5).scale(gloss * gloss + 0.04);
      std.specularPower = 8 + 120 * gloss * gloss;
      std.backFaceCulling = src.backFaceCulling;
      // Double-sided glTF materials: light back faces with the flipped normal.
      std.twoSidedLighting = !src.backFaceCulling;
      swapped.set(src, std);
    }
    mesh.material = std;
  }
  for (const [src] of swapped) {
    const i = c.materials.indexOf(src);
    if (i >= 0) c.materials.splice(i, 1);
    // Keep the texture: it now belongs to the StandardMaterial.
    src.dispose(false, false);
  }
}

/** Load the car model and build a visual (call once for the player, once for the ghost). */
export async function loadCarModel(scene: Scene, name: string, opts: CarModelOptions = {}): Promise<CarVisual> {
  const container = await loadContainer(scene, opts.source ?? CAR_MODEL_URL);
  const inst = container.instantiateModelsToScene((n) => `${name}-${n}`, true, { doNotInstantiate: true });

  const root = new TransformNode(name, scene);
  const gltfRoot = inst.rootNodes[0] as TransformNode | undefined;
  if (!gltfRoot) throw new Error('car.glb: empty scene');
  gltfRoot.parent = root;
  // The model's own root stays at the identity in the car frame (apart from the handedness flip).
  root.computeWorldMatrix(true);
  gltfRoot.computeWorldMatrix(true);

  const byName = new Map<string, TransformNode>();
  for (const n of gltfRoot.getDescendants(false)) byName.set(n.name.slice(name.length + 1), n as TransformNode);

  const frontWheels: TransformNode[] = [];
  const wheels: TransformNode[] = [];
  const wheelRadii: number[] = [];
  for (const wn of WHEEL_NAMES) {
    const node = byName.get(wn);
    if (!node) throw new Error(`car.glb: missing node ${wn}`);
    node.computeWorldMatrix(true);
    const pivot = new TransformNode(`${name}-${wn}`, scene);
    pivot.parent = root;
    // Root is at the identity here, so absolute = car-frame position.
    pivot.position.copyFrom(node.getAbsolutePosition());
    const spin = new TransformNode(`${name}-${wn}-spin`, scene);
    spin.parent = pivot;
    node.setParent(spin); // keeps its world transform (incl. the handedness flip)
    node.name = `${name}-${wn}-gltf`;
    if (wn === 'wheel_FL' || wn === 'wheel_FR') frontWheels.push(pivot);
    wheels.push(spin);
    wheelRadii.push(wn.startsWith('wheel_F') ? WHEEL_RADIUS.front : WHEEL_RADIUS.rear);
  }

  const materials: Material[] = [];
  const ghost = (opts.alpha ?? 1) < 1;
  const tint = opts.tint ?? new Color3(0.75, 0.85, 1);
  const meshes: AbstractMesh[] = root.getChildMeshes(false);
  for (const m of meshes) {
    m.isPickable = false;
    m.doNotSyncBoundingInfo = true;
    // The onboard camera always sees the car; skip per-frame frustum tests.
    m.alwaysSelectAsActiveMesh = true;
    const mat = m.material;
    if (mat && !materials.includes(mat)) materials.push(mat);
  }
  for (const mat of materials) {
    if (!(mat instanceof StandardMaterial)) continue;
    if (ghost) {
      // Plain pale tint (no livery) so the ghost reads as a ghost, as with the old placeholder.
      mat.diffuseTexture = null;
      mat.diffuseColor = tint.clone();
      mat.alpha = opts.alpha!;
      mat.transparencyMode = Material.MATERIAL_ALPHABLEND;
      // Glow a little so the ghost stays visible against dark asphalt.
      mat.emissiveColor = new Color3(0.35, 0.45, 0.6);
      mat.specularColor = new Color3(0.2, 0.2, 0.2);
      // Write depth first so overlapping parts of the translucent car don't stack up.
      mat.needDepthPrePass = true;
    } else {
      mat.freeze();
    }
  }
  if (ghost) {
    // Body before wheels: with the depth pre-pass the far-side wheels are then hidden by the body.
    for (const m of meshes) m.alphaIndex = wheels.some((w) => m.isDescendantOf(w)) ? 1001 : 1000;
  }
  return { root, frontWheels, wheels, materials, wheelRadii };
}

/** Roll the wheels by the distance travelled: `speed` (m/s) over `dt` (s). */
export function spinWheels(visual: CarVisual, dt: number, speed: number): void {
  const d = speed * dt;
  for (let i = 0; i < visual.wheels.length; i++) {
    const w = visual.wheels[i]!;
    // Keep the angle bounded so float precision doesn't degrade over a long session.
    w.rotation.x = (w.rotation.x + d / visual.wheelRadii[i]!) % (2 * Math.PI);
  }
}
